import * as React from "react"
import styled from 'styled-components'
import Layout, { Button } from '../components/Layout'
import SEO from '../components/SEO'
import mediaQuery from '../styles/mq'

export const Content = styled.div`
  font-family: ${(props) => props.theme.fonts.OpenSans};
  display: flex;
  flex-direction: column;
  height: auto;
  max-width: 800px;
  margin: 2.5rem auto;

  & > h1 {
    font-size: 48px;
    font-weight: 700;
    pointer-events: none;
    overflow-wrap: normal;
    max-width: 900px;
    text-align: center;
    line-height: 72px;
  }

  & > .report {
    width: 145px;
    align-self: center;
    margin: 2.5rem auto;
    padding: 1rem;
    background-color: ${(props) => props.theme.colors.common.white};
    color:  ${(props) => props.theme.colors.secondary};
  }

  & > div {
    h2 {
      margin-top: 1.75rem;
      font-size: 32px;
      font-weight: 300;
    }

    p {
      margin: 1rem 0 1.45rem;
      line-height: 36px;
      font-size: 15px;
      font-style: normal;
    }

    p, li {
      a {
        color: ${(props) => props.theme.colors.common.white};
        font-weight: bold;
        text-decoration: none;
        background-image: linear-gradient(currentColor, currentColor);
        background-position: 0% 100%;
        background-repeat: no-repeat;
        background-size: 0% 2px;
        transition: background-size cubic-bezier(0,.5,0,1) 0.3s;

        &:hover, &:focus {
          text-decoration: none;
          background-size: 100% 2px;
        }
      }
    }

    ul {
      line-height: 36px;
      font-size: 14px;
      font-style: normal;
      list-style-type: disc;
      padding: 0;
      margin: 1rem 0 1.45rem 1.45rem;
      list-style-position: outside;
      list-style-image: none;

      li {
        span {
          font-weight: bold;
          line-height: 22px;
        }
      }
    }

    ${mediaQuery('sm')(`
      width: 90%;
      margin: 0.1rem auto;
      h2 {
        text-align: center;
        font-size: 1.7rem;
        margin-top: 0.5rem;
        line-height: 34px;
      }
    `)}
  }

  & > .banner {
    margin: 1rem 0 3rem 0;
    padding: 1.5rem;
    border-radius: 10px;
    background-color: ${(props) => props.theme.colors.secondary};
    max-width: 440px;

    h2 {
      margin-top: 0.5rem;
      font-weight: 300;
      font-size: 32px;
      font-style: normal;
      line-height: 40px;
      margin-bottom: 1rem;
      float: left;
    }

    .report {
      color: ${(props) => props.theme.colors.secondary};
      width: 140px;
      padding: 1rem;
      float: right;
      font-weight: 600;
      font-size: 16px;
      line-height: 20px;
      display: flex;
      align-items: center;
      text-transform: uppercase;
      margin-top: 25px;
    }

    ${mediaQuery('sm')(`
      width: 80%;
      margin: 0.5rem;
      background-color: transparent;

      h2 {
        display: none;
      }

      .report {
        margin: 1rem auto;
        float: none;
      }
    `)}
  }

  ${mediaQuery('sm')(`
    width: 100%;
    margin: 0;

    & > h1 {
      font-size: 29px;
      text-align: center;
    }
  `)}
`;

const BugBountyPage = () => {
  return (
    <Layout>
        <SEO title="Energiswap: Bug Bounty" description="We rely on our community to enhance the security of Energiswap. Report bugs here and claim rewards." />
        <Content>
          <h1>Energiswap Bug Bounty</h1>
          <div>
            <p>
              As part of our ongoing efforts to ensure the security of Energiswap contracts, we have implemented a bug bounty reward program. 
              The security and stability of open source software like Energiswap is reliant on the feedback and testing efforts of community members. 
              By offering rewards for the discovery and reporting of certain high-value vulnerabilities, we hope to incentivize an ongoing process of community testing. 
              This will build on the extensive audits that Energiswap has already undergone to make the platform as safe as possible.
            </p>
          </div>
          <div>
            <h2>Scope</h2>
            <p>This program is limited to the reporting of vulnerabilities affecting the following contracts:</p>
              <ul>
                <li>Energiswap Contracts</li>
                <li>Energiswap Governance Contracts</li>
                <li>Energi Wrapped Token Contracts</li>
              </ul>
          </div>
          <div>
            <h2>Qualifying Vulnerabilities</h2>
            <p>At this time, rewards will only be paid out for the discovery of vulnerabilities in the Energiswap core smart contracts listed above.</p>
            <p>Exploits may be grouped in the following ways:</p>
              <ul>
                <li><span>Function-level:</span> exploitable through a single entry-point.</li>
                <li><span>Contract-level:</span> combining multiple entry-points.</li>
                <li><span>System-level:</span> combining multiple contracts.</li>
                <li><span>Game-level:</span> attacking the incentive mechanisms. (currently not eligible for reward)</li>
              </ul>
          </div>
          <div>
            <h2>Non-Qualifying Vulnerabilities</h2>
              <ul>
                <li>The example contracts and the contracts in the test folder.</li>
                <li>Any contract removed from the Energiswap Contracts & Energiswap Governance Contracts lists (these lists may change from time to time without notice).</li>
                <li>Bugs in any third-party contract or platform that interacts with Energiswap Contracts.</li>
                <li>Vulnerabilities already reported and/or discovered in contracts built by third parties on Energiswap.</li>
              </ul>
              <ul>
                <li>Any bugs that have already been reported.</li>
                <li>DDOS attacks.</li>
                <li>Spamming.</li>
                <li>Automated tools.</li>
                <li>Compromising or misusing third party systems or services.</li>
              </ul>
          </div>
          <div>
            <h2>Bug Rating / Reward</h2>
            <p>The severity of bugs will be assessed under the <a href="https://www.first.org/cvss/calculator/3.0" target="_blank" rel="noreferrer">CVSS Risk Rating</a> scale as follows:</p>
            <ul>
              <li>Critical (9.0-10.0):<span> 1300 - 2100 NRG</span></li>
              <li>High (7.0-8.9):<span> 700 - 900 NRG</span></li>
              <li>Medium (4.0-6.9):<span> 300 - 500 NRG</span></li>
              <li>Low (0.1-3.9):<span> 100 - 200 NRG</span></li>
            </ul>
          </div>
          <div>
            <h2>Reporting Process</h2>
            <p>To be eligible for reward under the program, all bugs or vulnerabilities must be reported exclusively to <a href="mailto:contact@energi.team">contact@energi.team</a> without being disclosed to any other parties, 
              either public or private. Please include as much detail as you possibly can, including:
            </p>
            <ul>
              <li>The environment conditions in which the bug was produced.</li>
              <li>The steps required to reproduce the bug.</li>
              <li>The potential impact of the vulnerability being exploited.</li>
            </ul>
            <p>The more detail and evidence included in your report, the more likely it is that you will qualify for a reward.</p>
          </div>
          <div>
            <h2>Reporter Eligibility</h2>
            <p>In order to be eligible for a reward, you must:</p>
            <ul>
              <li>Be the first person to report the vulnerability to the <span><a href="mailto:contact@energi.team">contact@energi.team</a></span> account.</li>
              <li>Submit only one vulnerability per submission, unless you need to chain vulnerabilities to provide impact regarding any of the vulnerabilities.</li>
              <li>Provide sufficiently clear instructions for our engineers to reproduce and fix the vulnerability.</li>
              <li>Not exploit the vulnerability yourself or make it public.</li>
              <li>Not engage in any illegal activity in the discovery or reporting process.</li>
              <li>Not be one of our current or former employees, vendors, or contractors (or an employee of any of those vendors or contractors).</li>
              <li>Comply with all the eligibility requirements of the program.</li>
            </ul>
          </div>
          <div>
            <h2>Estimated Processing Time</h2>
            <p>This is the estimated time it would normally take to process a bug report:</p>
            <ul>
              <li>First response (to support ticket) - <span>2 days</span></li>
              <li>Report processing - <span>14 days</span></li>
              <li>Sign-off / Pay-out - <span>7 days</span></li>
            </ul>
          </div>
          <div className="banner">
            <h2>Found a bug<br></br>that qualifies?</h2>
            <Button href="mailto:contact@energi.team" className="report">Submit Report</Button>
          </div>
        </Content> 
    </Layout>
  )
}

export default BugBountyPage
